import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft, FaUser, FaFlask, FaMapMarkerAlt, FaWallet, FaCheck,
} from "react-icons/fa";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { getWallet } from "../api/staff.api";
import { createDiagnosticBookings, createPackageBooking } from "../api/bookings.api";
import { submitBookingWithPayment } from "../utils/bookingPayment";
import { apiErrorMessage } from "../utils/apiError";
import { getStaffId } from "../utils/storage";
import { ITEM_TYPE, formatPrice } from "../utils/labTests";
import { toast } from "../Components/Toast";
import {
  getLabWalletBalance,
  getPackageWalletBalance,
  getCategoryWalletAllocation,
  formatWalletAmount,
} from "../utils/wallet";

const formatDateLong = (ymd) => {
  const d = new Date(ymd + "T00:00:00");
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" });
};

const LabTestsReviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const staffId = getStaffId();
  const draft = location.state || {};

  const {
    lineItems = [],
    itemType,
    patient,
    address,
    diagnostic,
    serviceType,
    selectedDate,
    selectedTime,
    selectedSlot,
  } = draft;

  const [walletData, setWalletData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!lineItems.length || !patient || !diagnostic || !selectedDate || !selectedTime) {
      navigate("/lab-tests", { replace: true });
    }
  }, [lineItems, patient, diagnostic, selectedDate, selectedTime, navigate]);

  useEffect(() => {
    if (!staffId) return;
    getWallet(staffId)
      .then((res) => setWalletData(res.data))
      .catch(() => {});
  }, [staffId]);

  const subtotal = useMemo(
    () => lineItems.reduce((s, i) => s + (Number(i.price) || 0), 0),
    [lineItems]
  );

  const isPackage = itemType === ITEM_TYPE.package;
  const walletBal = isPackage
    ? getPackageWalletBalance(walletData)
    : getLabWalletBalance(walletData);
  const categoryAlloc = getCategoryWalletAllocation(
    walletData,
    isPackage ? "package" : "lab"
  );
  const walletUse = Math.min(subtotal, walletBal);
  const canPayWithWallet = walletBal >= subtotal;
  const shortfall = Math.max(0, subtotal - walletBal);
  const fastingNote = lineItems.some((i) => i.fastingRequired);

  if (!lineItems.length || !patient || !diagnostic || !selectedDate || !selectedTime) return null;

  const bookingBase = () => ({
    familyMemberId: patient.familyMemberId,
    diagnosticId: diagnostic._id,
    serviceType,
    date: selectedDate,
    timeSlot: selectedTime,
    addressId: serviceType === "Home Collection" && address?._id ? address._id : null,
  });

  const goConfirmed = (bookingResponse) => {
    toast.success("Lab test order placed successfully.");
    navigate("/lab-tests/confirmed", {
      replace: true,
      state: {
        lineItems,
        patient,
        diagnostic,
        serviceType,
        selectedDate,
        selectedTime,
        selectedSlot,
        booking: bookingResponse,
        walletUsed: walletUse,
        amountPaid: subtotal,
        fastingNote,
      },
    });
  };

  const razorpayDescription = isPackage
    ? `Package: ${lineItems[0]?.title || "Health package"}`
    : `Lab tests at ${diagnostic.name}`;

  const handleConfirm = async () => {
    setError("");
    setProcessing(true);
    try {
      const pkg = lineItems[0];
      const body = isPackage ? { ...bookingBase(), packageId: pkg.id } : bookingBase();
      const data = await submitBookingWithPayment({
        post: (payload) =>
          isPackage
            ? createPackageBooking(staffId, payload)
            : createDiagnosticBookings(staffId, payload),
        body,
        description: razorpayDescription,
        refreshCart: !isPackage,
      });
      goConfirmed(data);
    } catch (err) {
      const msg = apiErrorMessage(err, "Booking failed. Please try again.");
      if (msg === "Payment cancelled") {
        toast.error("Payment cancelled");
      } else {
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="bg-surface-low min-h-screen flex flex-col">
      <Header />
      <div className="flex flex-col min-h-screen pb-16 lg:pb-0 flex-1">
        <main className="flex-1 max-w-7xl mx-auto w-full px-3 sm:px-6 py-4 sm:py-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-on-surface-variant hover:text-primary mb-3"
          >
            <FaArrowLeft size={12} /> Back to Selection
          </button>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary mb-4 sm:mb-6">Order Summary</h1>
          {error && <p className="text-error text-sm mb-4">{error}</p>}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2 space-y-3 sm:space-y-4 min-w-0">
              <div className="bg-surface-lowest rounded-xl border border-surface-container p-3 sm:p-5">
                <h2 className="flex items-center gap-2 font-semibold text-primary mb-4">
                  <FaFlask /> Selected Tests
                </h2>
                <div className="space-y-3">
                  {lineItems.map((item) => (
                    <div key={item.id} className="flex justify-between gap-3 pb-3 border-b border-surface-container last:border-0 last:pb-0 min-w-0">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm sm:text-base break-words">{item.title}</p>
                        {item.description && <p className="text-xs sm:text-sm text-on-surface-variant line-clamp-2">{item.description}</p>}
                      </div>
                      <p className="font-bold shrink-0 text-sm sm:text-base">{formatPrice(item.price)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-surface-lowest rounded-xl border border-surface-container p-3 sm:p-5 min-w-0">
                  <h2 className="flex items-center gap-2 font-semibold text-primary mb-3"><FaUser /> Patient Details</h2>
                  <p className="font-medium text-sm sm:text-base break-words">{patient.name}</p>
                  <p className="text-xs sm:text-sm text-on-surface-variant mt-1">{formatDateLong(selectedDate)}</p>
                  <p className="text-xs sm:text-sm text-on-surface-variant">{selectedTime}</p>
                </div>
                <div className="bg-surface-lowest rounded-xl border border-surface-container p-3 sm:p-5 min-w-0">
                  <h2 className="flex items-center gap-2 font-semibold text-primary mb-3"><FaMapMarkerAlt /> Preferred Lab</h2>
                  <p className="font-medium text-sm sm:text-base truncate">{diagnostic.name}</p>
                  <p className="text-xs sm:text-sm text-on-surface-variant mt-1">{serviceType}</p>
                  {address && (
                    <p className="text-xs sm:text-sm text-on-surface-variant mt-1 break-words">
                      {address.street}, {address.city}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <aside className="bg-surface-lowest rounded-xl border border-surface-container p-3 sm:p-5 h-fit lg:sticky lg:top-4">
              <h2 className="font-semibold text-primary mb-4">Payment Summary</h2>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-on-surface-variant">Subtotal ({lineItems.length} item{lineItems.length !== 1 ? "s" : ""})</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                {walletUse > 0 && (
                  <div className="flex justify-between text-primary">
                    <span>Enterprise / wallet subsidy</span>
                    <span>-{formatPrice(walletUse)}</span>
                  </div>
                )}
              </div>

              {walletData && (
                <div className="bg-primary-fixed/30 rounded-lg px-3 py-2 mb-4 text-xs sm:text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <FaWallet className="text-primary shrink-0" />
                    <span className="break-words">
                      Available corporate balance: ₹{formatWalletAmount(walletBal)}
                    </span>
                  </div>
                  {categoryAlloc != null && categoryAlloc !== walletBal && (
                    <p className="text-on-surface-variant pl-6">
                      Category allocation: ₹{formatWalletAmount(categoryAlloc)} (total balance used for booking)
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-between items-center border-t border-surface-container pt-4 mb-2">
                <span className="font-bold">Paid from wallet</span>
                <span className="text-xl font-bold text-primary">{formatPrice(walletUse)}</span>
              </div>
              {!canPayWithWallet && (
                <p className="text-sm text-error mb-4">
                  Shortfall: {formatPrice(shortfall)}.{" "}
                  <Link to="/wallet" className="underline font-medium">View wallet</Link>
                  <span className="block mt-1 text-on-surface-variant">
                    Remaining {formatPrice(shortfall)} will be paid online.
                  </span>
                </p>
              )}

              <button
                type="button"
                onClick={handleConfirm}
                disabled={processing}
                className="w-full py-3 rounded-lg bg-primary text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <FaCheck /> {processing ? "Processing..." : shortfall > 0 ? "Pay & Book" : "Confirm Order"}
              </button>
            </aside>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default LabTestsReviewPage;
